import React, { createContext, useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (token: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const checkTokenValidity = (token: string): Promise<boolean> => {
  return new Promise((resolve) => {
    // Here you could add actual token validation logic
    // For example, checking if the token is expired
    // For now, we'll just check if it exists
    resolve(!!token);
  });
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { push } = useHistory();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (token) {
          const isValid = await checkTokenValidity(token);
          if (isValid) {
            setIsAuthenticated(true);
            push('/');
          } else {
            // Token exists but is invalid
            localStorage.removeItem('token');
            setIsAuthenticated(false);
            push('/login');
          }
        } else {
          setIsAuthenticated(false);
          push('/login');
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        push('/login');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [push]);

  const login = (token: string) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    push('/');
  };

  const logout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    push('/login');
  };

  if (isLoading) {
    return null; // Or a loading spinner component
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}; 